import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Clock, Activity, RefreshCw, AlertTriangle, ArrowRight, X } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Login sonrası karşılama modalı.
 * Beklemede / İşlemde / İncelemede aktif görev sayıları + en çok bekleyen 5 görev.
 * Görünüm kuralı:
 *   - Her login'de (her oturum başlangıcında) 1 kez gösterilir.
 *   - "Bugün gösterilmedi" check'i sessionStorage üzerinden yapılır.
 *   - total === 0 ise gösterilmez.
 * Modal'da "Görevlerime Git" → /tasks?myActive=1 (Aktif Görevlerim).
 */
export default function ActiveTasksWelcomeModal() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Her login'de göster: localStorage tekrarını kaldırdık.
    // Aynı oturum içinde tekrar gösterimi engellemek için sessionStorage
    // bayrağı kullanılır (sayfa yenilemede tekrar gelmesin).
    const sessionKey = `welcome_session_${user.id}`;
    if (sessionStorage.getItem(sessionKey)) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/users/me/active-tasks-summary?limit=5`);
        if (cancelled) return;
        const data = res.data || {};
        if ((data.total || 0) > 0) {
          setSummary(data);
          setOpen(true);
          sessionStorage.setItem(sessionKey, '1');
        }
      } catch (err) {
        console.warn('Active tasks summary load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!open || !summary) return null;

  const counts = summary.counts || {};
  const items = summary.longestWaiting || [];

  const goTasks = () => {
    setOpen(false);
    navigate('/tasks?myActive=1');
  };

  const priorityColor = (p) => {
    if (p === 'urgent') return 'bg-red-100 text-red-700 border-red-300';
    if (p === 'high') return 'bg-orange-100 text-orange-700 border-orange-300';
    if (p === 'medium') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };
  const priorityLabel = (p) => {
    return { urgent: 'Acil', high: 'Yüksek', medium: 'Orta', low: 'Düşük' }[p] || p || '-';
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[10003] flex items-center justify-center p-3 sm:p-4"
      data-testid="welcome-active-tasks-modal"
    >
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="relative px-5 sm:px-7 pt-6 pb-8 rounded-t-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white">
          <button
            onClick={() => setOpen(false)}
            data-testid="welcome-modal-close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition"
            aria-label="Kapat"
          >
            <X className="w-4 h-4" />
          </button>
          <h2 className="text-xl sm:text-2xl font-bold leading-tight">
            Merhaba {user?.name || ''} 👋
          </h2>
          <p className="mt-1 text-white/90 text-sm sm:text-base">
            Seni bekleyen <span className="font-bold">{summary.total} aktif görev</span> var.
          </p>
        </div>

        {/* Counts grid */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 px-5 sm:px-7 mt-4">
          <StatusCard
            label="Beklemede"
            count={counts['beklemede'] || 0}
            color="amber"
            icon={<Clock className="w-5 h-5 sm:w-6 sm:h-6" />}
          />
          <StatusCard
            label="İşlemde"
            count={counts['işlemde'] || 0}
            color="blue"
            icon={<Activity className="w-5 h-5 sm:w-6 sm:h-6" />}
          />
          <StatusCard
            label="İncelemede"
            count={counts['incelemede'] || 0}
            color="orange"
            icon={<RefreshCw className="w-5 h-5 sm:w-6 sm:h-6" />}
          />
        </div>

        {/* Longest waiting */}
        {items.length > 0 && (
          <div className="px-5 sm:px-7 pt-5 pb-2">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <h3 className="text-sm font-semibold text-gray-800">
                En Çok Bekleyen Görevler
              </h3>
            </div>
            <ul className="space-y-2">
              {items.map((t) => (
                <li
                  key={t.id}
                  data-testid={`welcome-task-row-${t.id}`}
                  className="flex items-center gap-2 sm:gap-3 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/tasks?openTask=${t.id}&myActive=1`);
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{t.title}</div>
                    <div className="text-[11px] text-gray-500 truncate">{t.projectName || '—'}</div>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${priorityColor(t.priority)} flex-shrink-0`}>
                    {priorityLabel(t.priority)}
                  </span>
                  <span
                    className={`text-[11px] font-semibold flex-shrink-0 ${
                      (t.stepDays || 0) >= 7
                        ? 'text-red-600'
                        : (t.stepDays || 0) >= 3
                        ? 'text-amber-600'
                        : 'text-gray-600'
                    }`}
                  >
                    {t.waitText || '—'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 sm:px-7 py-4 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
          <button
            data-testid="welcome-modal-dismiss"
            onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition order-2 sm:order-1"
          >
            Şimdi Değil
          </button>
          <button
            data-testid="welcome-modal-go-tasks"
            onClick={goTasks}
            className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition flex items-center justify-center gap-2 order-1 sm:order-2"
          >
            Görevlerime Git
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function StatusCard({ label, count, color, icon }) {
  const palette = {
    amber: 'from-amber-50 to-amber-100 text-amber-700 border-amber-200',
    blue: 'from-blue-50 to-blue-100 text-blue-700 border-blue-200',
    orange: 'from-orange-50 to-orange-100 text-orange-700 border-orange-200',
  }[color];
  return (
    <div
      data-testid={`welcome-status-card-${label.toLowerCase()}`}
      className={`rounded-xl border ${palette} bg-gradient-to-br p-3 sm:p-4 shadow-sm`}
    >
      <div className="flex items-center justify-between">
        <div className="opacity-80">{icon}</div>
        <div className="text-2xl sm:text-3xl font-bold leading-none">{count}</div>
      </div>
      <div className="mt-1 text-[11px] sm:text-xs font-semibold opacity-90">{label}</div>
    </div>
  );
}
